import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { getSiteSettings, updateSiteSettings } from '../services/api';
import { Loader2, Save } from 'lucide-react';

export const HomeContentManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSiteSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSiteSettings(settings);
      toast.success('Homepage content updated successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-otter-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold dark:text-white">Homepage Content</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage homepage hero and feature sections</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-otter-green hover:bg-otter-green-600">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Hero Section */}
      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="dark:text-white">Hero Section</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="home_hero_title">Hero Title</Label>
            <Input
              id="home_hero_title"
              value={settings.home_hero_title || ''}
              onChange={(e) => updateField('home_hero_title', e.target.value)}
              placeholder="Find Your Perfect Home in Lynchburg"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="home_hero_subtitle">Hero Subtitle</Label>
            <Textarea
              id="home_hero_subtitle"
              value={settings.home_hero_subtitle || ''}
              onChange={(e) => updateField('home_hero_subtitle', e.target.value)}
              placeholder="Quality property management services..."
              rows={3}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Why Choose Section */}
      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="dark:text-white">Why Choose Section</CardTitle>
            <div className="flex items-center space-x-2">
              <Label htmlFor="why_choose_enabled" className="text-sm">
                Enable Section
              </Label>
              <Switch
                id="why_choose_enabled"
                checked={settings.why_choose_enabled}
                onCheckedChange={(checked) => updateField('why_choose_enabled', checked)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="why_choose_title">Section Title</Label>
            <Input
              id="why_choose_title"
              value={settings.why_choose_title || ''}
              onChange={(e) => updateField('why_choose_title', e.target.value)}
              placeholder="Why Choose Otter River Properties?"
              className="mt-1"
              disabled={!settings.why_choose_enabled}
            />
          </div>
          <div>
            <Label htmlFor="why_choose_subtitle">Section Subtitle</Label>
            <Textarea
              id="why_choose_subtitle"
              value={settings.why_choose_subtitle || ''}
              onChange={(e) => updateField('why_choose_subtitle', e.target.value)}
              placeholder="Over 15 years of dedicated service..."
              rows={2}
              className="mt-1"
              disabled={!settings.why_choose_enabled}
            />
          </div>
        </CardContent>
      </Card>

      {/* Feature Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Feature 1 */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg dark:text-white">Feature 1</CardTitle>
              <Switch
                checked={settings.feature_1_enabled}
                onCheckedChange={(checked) => updateField('feature_1_enabled', checked)}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="feature_1_title">Title</Label>
              <Input
                id="feature_1_title"
                value={settings.feature_1_title || ''}
                onChange={(e) => updateField('feature_1_title', e.target.value)}
                disabled={!settings.feature_1_enabled}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="feature_1_description">Description</Label>
              <Textarea
                id="feature_1_description"
                value={settings.feature_1_description || ''}
                onChange={(e) => updateField('feature_1_description', e.target.value)}
                disabled={!settings.feature_1_enabled}
                rows={3}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Feature 2 */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg dark:text-white">Feature 2</CardTitle>
              <Switch
                checked={settings.feature_2_enabled}
                onCheckedChange={(checked) => updateField('feature_2_enabled', checked)}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="feature_2_title">Title</Label>
              <Input
                id="feature_2_title"
                value={settings.feature_2_title || ''}
                onChange={(e) => updateField('feature_2_title', e.target.value)}
                disabled={!settings.feature_2_enabled}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="feature_2_description">Description</Label>
              <Textarea
                id="feature_2_description"
                value={settings.feature_2_description || ''}
                onChange={(e) => updateField('feature_2_description', e.target.value)}
                disabled={!settings.feature_2_enabled}
                rows={3}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Feature 3 */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg dark:text-white">Feature 3</CardTitle>
              <Switch
                checked={settings.feature_3_enabled}
                onCheckedChange={(checked) => updateField('feature_3_enabled', checked)}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="feature_3_title">Title</Label>
              <Input
                id="feature_3_title"
                value={settings.feature_3_title || ''}
                onChange={(e) => updateField('feature_3_title', e.target.value)}
                disabled={!settings.feature_3_enabled}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="feature_3_description">Description</Label>
              <Textarea
                id="feature_3_description"
                value={settings.feature_3_description || ''}
                onChange={(e) => updateField('feature_3_description', e.target.value)}
                disabled={!settings.feature_3_enabled}
                rows={3}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Feature 4 */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg dark:text-white">Feature 4</CardTitle>
              <Switch
                checked={settings.feature_4_enabled}
                onCheckedChange={(checked) => updateField('feature_4_enabled', checked)}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="feature_4_title">Title</Label>
              <Input
                id="feature_4_title"
                value={settings.feature_4_title || ''}
                onChange={(e) => updateField('feature_4_title', e.target.value)}
                disabled={!settings.feature_4_enabled}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="feature_4_description">Description</Label>
              <Textarea
                id="feature_4_description"
                value={settings.feature_4_description || ''}
                onChange={(e) => updateField('feature_4_description', e.target.value)}
                disabled={!settings.feature_4_enabled}
                rows={3}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
